/**
 * The site admin page: people, games, content and the server, across every account. Only the
 * account named by the server's `ADMIN_EMAIL` gets data; anyone else gets the server's 404.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, type SiteReport, type SiteStats } from '../lib/api';
import { AuthGate } from '../components/AuthGate';

export function SiteAdmin() {
  return (
    <AuthGate title="Site admin">
      <SiteAdminInner />
    </AuthGate>
  );
}

type TallyRow = { key: keyof SiteStats; label: string };

const PEOPLE: TallyRow[] = [
  { key: 'users', label: 'Users joined' },
  { key: 'players', label: 'Player seats' },
  { key: 'signedInPlayers', label: 'Signed-in seats' },
  { key: 'friendAdds', label: 'Friend adds' },
];

const GAMES: TallyRow[] = [
  { key: 'gamesPlayed', label: 'Games played' },
  { key: 'gamesFinished', label: 'Games finished' },
  { key: 'playNowGames', label: 'Play Now games' },
  { key: 'questions', label: 'Questions added' },
];

const n = (value: number) => value.toLocaleString();
const date = (ms: number) => new Date(ms).toLocaleDateString();

function bytes(value: number): string {
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function duration(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return d > 0 ? `${String(d)}d ${String(h)}h` : `${String(h)}h ${String(m)}m`;
}

function SiteAdminInner() {
  const [report, setReport] = useState<SiteReport | null>(null);
  const [gameNames, setGameNames] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.siteStats(), api.listGames()]).then(
      ([data, games]) => {
        setReport(data);
        setGameNames(new Map(games.map((g) => [g.id, g.name])));
      },
      (err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not load the site stats.');
      },
    );
  }, []);

  const game = (id: string | null) => (id ? (gameNames.get(id) ?? id) : '—');

  return (
    <div className="admin">
      <div className="admin-header">
        <h1>Site admin</h1>
        <span className="spacer" />
        <Link className="btn btn-sm" to="/">
          Home
        </Link>
      </div>

      {error ? <p className="faint">{error}</p> : null}
      {report ? (
        <div className="account-grid">
          <div className="col">
            <Section title="People">
              <TallyTable report={report} rows={PEOPLE} />
              <p className="faint">
                Active users: {n(report.activeUsers.week)} this week, {n(report.activeUsers.month)}{' '}
                this month
              </p>
              <h3>Newest sign-ups</h3>
              <Table
                head={['Name', 'Email', 'Joined']}
                rows={report.newestAccounts.map((a) => [a.displayName, a.email ?? '—', date(a.createdAt)])}
                empty="No accounts yet."
              />
            </Section>

            <Section title="Server">
              <Table
                head={['', '']}
                rows={[
                  ['Version', report.server.version],
                  ['Node', report.server.node],
                  ['Uptime', duration(report.server.uptimeSeconds)],
                  ['Memory', bytes(report.server.rssBytes)],
                  ['Database', bytes(report.server.databaseBytes)],
                  ['Expired logins', n(report.server.expiredLogins)],
                  ['Armed timers', n(report.server.armedTimers)],
                ]}
              />
            </Section>
          </div>

          <div className="col">
            <Section title={`Live now (${String(report.live.length)})`}>
              <Table
                head={['Code', 'Title', 'Game', 'Online / seats']}
                rows={report.live.map((s) => [
                  s.code,
                  s.playNow ? `${s.title} (Play Now)` : s.title,
                  game(s.gameId),
                  `${n(s.connected)} / ${n(s.players)}`,
                ])}
                empty="No games in progress."
              />
            </Section>

            <Section title="Games">
              <TallyTable report={report} rows={GAMES} />
              <h3>Plays per game</h3>
              <Table
                head={['Game', 'Rounds played']}
                rows={report.playsByGame.map((g) => [game(g.gameId), n(g.plays)])}
                empty="Nothing played yet."
              />
            </Section>

            <Section title="Top categories">
              <Table
                head={['Category', 'Questions']}
                rows={report.topCategories.map((c) => [c.category, n(c.questions)])}
                empty="No tagged questions yet."
              />
            </Section>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="panel col site-stats">
      <h2 className="panel-title">{title}</h2>
      {children}
    </section>
  );
}

function TallyTable({ report, rows }: { report: SiteReport; rows: TallyRow[] }) {
  return (
    <Table
      head={['', 'All time', `Last ${String(report.recentDays)} days`]}
      rows={rows.map(({ key, label }) => [label, n(report.stats[key].total), n(report.stats[key].recent)])}
    />
  );
}

/** The first cell of each row is its header. */
function Table({ head, rows, empty }: { head: string[]; rows: string[][]; empty?: string }) {
  if (rows.length === 0) return <p className="faint">{empty}</p>;
  return (
    <table>
      <thead>
        <tr>
          {head.map((label, i) => (
            <th key={i} scope="col">
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([first, ...rest], i) => (
          <tr key={i}>
            <th scope="row">{first}</th>
            {rest.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
